import React from 'react';

type TableRootProps = React.ComponentPropsWithoutRef<'table'> & {
  wrapperClassName?: string;
};

type TableHeadProps = React.ComponentPropsWithoutRef<'thead'>;
type TableBodyProps = React.ComponentPropsWithoutRef<'tbody'>;
type TableRowProps = React.ComponentPropsWithoutRef<'tr'>;
type TableHeaderCellProps = React.ComponentPropsWithoutRef<'th'>;
type TableCellProps = React.ComponentPropsWithoutRef<'td'>;

const TableRoot = ({
  wrapperClassName,
  className,
  children,
  ...props
}: TableRootProps): React.ReactElement => {
  const finalWrapperClass = wrapperClassName
    ? `app-table-scroll-wrap overflow-x-auto ${wrapperClassName}`
    : 'app-table-scroll-wrap overflow-x-auto';
  const finalClassName = className
    ? `min-w-full divide-y divide-gray-200 ${className}`
    : 'min-w-full divide-y divide-gray-200';

  return (
    <div className={finalWrapperClass}>
      <table className={finalClassName} {...props}>
        {children}
      </table>
    </div>
  );
};

const TableHead = ({ className, children, ...props }: TableHeadProps): React.ReactElement => (
  <thead className={className ?? 'bg-gray-50'} {...props}>
    {children}
  </thead>
);

const TableBody = ({ className, children, ...props }: TableBodyProps): React.ReactElement => (
  <tbody className={className ?? 'bg-white divide-y divide-gray-200'} {...props}>
    {children}
  </tbody>
);

const TableRow = ({ className, children, ...props }: TableRowProps): React.ReactElement => (
  <tr className={className} {...props}>
    {children}
  </tr>
);

const TableHeaderCell = ({ className, children, ...props }: TableHeaderCellProps): React.ReactElement => (
  <th className={className ?? 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'} {...props}>
    {children}
  </th>
);

const TableCell = ({ className, children, ...props }: TableCellProps): React.ReactElement => (
  <td className={className ?? 'px-4 py-3 text-sm text-gray-900 align-top'} {...props}>
    {children}
  </td>
);

type TableComponent = typeof TableRoot & {
  Head: typeof TableHead;
  Thead: typeof TableHead;
  Body: typeof TableBody;
  Tbody: typeof TableBody;
  Row: typeof TableRow;
  HeaderCell: typeof TableHeaderCell;
  HCell: typeof TableHeaderCell;
  Cell: typeof TableCell;
};

const Table = TableRoot as TableComponent;
Table.Head = TableHead;
Table.Thead = TableHead;
Table.Body = TableBody;
Table.Tbody = TableBody;
Table.Row = TableRow;
Table.HeaderCell = TableHeaderCell;
Table.HCell = TableHeaderCell;
Table.Cell = TableCell;

export { Table };
export type { TableRootProps };
